import type { ErrorInfo } from "react";
import type { FCIError } from "@searchui-searchforms/shared/fci";
import { useLogFCI } from "./useLogFCI";

/**
 * Adapter between React error boundaries and `useLogFCI`.
 *
 * Error boundaries expect an `(error, errorInfo) => void` callback, while
 * `useLogFCI` accepts `LogArgs`. This hook bridges the two: it extracts
 * `stack` and `componentStack` from the error boundary arguments and
 * delegates to `useLogFCI` so every logged error automatically includes
 * FCI context details (formName, formType, etc.).
 *
 * Use `useLogFCI` directly for ad-hoc logging outside error boundaries.
 */
export const useErrorBoundaryLogger = () => {
    const logFCI = useLogFCI();

    return (errorType: FCIError) => (error: unknown, errorInfo: ErrorInfo) => {
        logFCI({
            errorType,
            errorInfo: `ErrorBoundary caught an error: ${error}`,
            details: {
                stack: error instanceof Error ? error.stack : undefined,
                componentStack: errorInfo.componentStack ?? undefined,
            },
        });
    };
};
